import styled, { css } from "styled-components";

const publicBackground = `
  radial-gradient(circle at top left, rgba(124, 58, 237, 0.18), transparent 24%),
  linear-gradient(180deg, #f7f8ff 0%, #f8fafc 38%, #eef2ff 100%)
`;

const appBackground = `
  radial-gradient(circle at top left, rgba(124, 58, 237, 0.14), transparent 24%),
  linear-gradient(180deg, #09090f 0%, #101323 38%, #171d35 100%)
`;

const cardBackground = (variant) =>
  variant === "dark"
    ? "rgba(15, 23, 42, 0.72)"
    : "rgba(255, 255, 255, 0.82)";

const cardBorder = (variant) =>
  variant === "dark"
    ? "1px solid rgba(148, 163, 184, 0.16)"
    : "1px solid rgba(226, 232, 240, 0.95)";

export const AppPageShell = styled.div`
  min-height: 100vh;
  width: 100%;
  padding: ${(p) => p.$compact ? "5rem 1rem 2rem" : "5.5rem 1rem 2rem"};
  background: ${(p) => (p.$variant === "light" ? publicBackground : appBackground)};
  color: ${(p) => (p.$variant === "light" ? "#172033" : "#e5e7eb")};

  @media (max-width: 768px) {
    padding-top: 5rem;
  }
`;

export const AppPageContent = styled.div`
  max-width: ${(p) => p.$width || "1280px"};
  margin: 0 auto;
`;

export const AppHero = styled.section`
  display: grid;
  gap: 0.9rem;
  margin-bottom: 1.25rem;
  padding: ${(p) => (p.$tight ? "1.25rem" : "1.6rem")};
  border-radius: 28px;
  border: ${(p) => (p.$variant === "light"
    ? "1px solid rgba(255,255,255,0.92)"
    : "1px solid rgba(148,163,184,0.16)")};
  background: ${(p) => (p.$variant === "light"
    ? "rgba(255,255,255,0.84)"
    : "rgba(15,23,42,0.7)")};
  backdrop-filter: blur(12px);
`;

export const AppEyebrow = styled.div`
  color: ${(p) => (p.$variant === "light" ? "#7c3aed" : "#c4b5fd")};
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const AppTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.8rem, 3vw, 2.8rem);
  line-height: 1.08;
  letter-spacing: -0.03em;
  color: ${(p) => (p.$variant === "light" ? "#0f172a" : "#ffffff")};
`;

export const AppSubtitle = styled.p`
  margin: 0;
  max-width: 880px;
  color: ${(p) => (p.$variant === "light" ? "#64748b" : "rgba(255,255,255,0.72)")};
  font-size: 0.98rem;
  line-height: 1.7;
`;

export const AppMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
`;

export const AppPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: ${(p) => (p.$small ? "0.28rem 0.55rem" : "0.45rem 0.8rem")};
  border-radius: 999px;
  border: ${(p) => (p.$active
    ? "1px solid rgba(124,58,237,0.28)"
    : "1px solid rgba(148,163,184,0.18)")};
  background: ${(p) => (p.$active
    ? "rgba(124,58,237,0.12)"
    : "rgba(255,255,255,0.06)")};
  color: ${(p) => (p.$active ? "#7c3aed" : p.$variant === "light" ? "#475569" : "rgba(255,255,255,0.72)")};
  font-size: ${(p) => (p.$small ? "0.74rem" : "0.82rem")};
  font-weight: 700;
`;

export const AppSurfaceGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: ${(p) => p.$columns || "repeat(auto-fit, minmax(260px, 1fr))"};
`;

export const AppSectionCard = styled.section`
  padding: ${(p) => (p.$compact ? "1rem" : "1.2rem")};
  border-radius: 22px;
  background: ${(p) => cardBackground(p.$variant)};
  border: ${(p) => cardBorder(p.$variant)};
  backdrop-filter: blur(12px);
`;

export const AppSectionTitle = styled.h2`
  margin: 0;
  color: ${(p) => (p.$variant === "light" ? "#0f172a" : "#ffffff")};
  font-size: 1.08rem;
  font-weight: 800;
`;

export const AppSectionHint = styled.p`
  margin: 0.45rem 0 0;
  color: ${(p) => (p.$variant === "light" ? "#64748b" : "rgba(255,255,255,0.6)")};
  font-size: 0.88rem;
  line-height: 1.6;
`;

export const AppSectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: ${(p) => (p.$tight ? "0.75rem" : "1rem")};
  flex-wrap: wrap;
`;

export const AppActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 42px;
  padding: 0.75rem 1rem;
  border-radius: 14px;
  border: 1px solid transparent;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.2s ease, background 0.2s ease;
  text-decoration: none;

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const AppButton = styled.button`
  ${buttonBase}
  background: ${(p) => (p.$secondary ? "rgba(255,255,255,0.06)" : "#7c3aed")};
  color: ${(p) => (p.$secondary ? (p.$variant === "light" ? "#334155" : "#e2e8f0") : "#ffffff")};
  border-color: ${(p) => (p.$secondary ? "rgba(148,163,184,0.2)" : "rgba(124,58,237,0.34)")};
`;

export const AppLinkButton = styled.a`
  ${buttonBase}
  background: ${(p) => (p.$secondary ? "rgba(255,255,255,0.06)" : "#7c3aed")};
  color: ${(p) => (p.$secondary ? (p.$variant === "light" ? "#334155" : "#e2e8f0") : "#ffffff")};
  border-color: ${(p) => (p.$secondary ? "rgba(148,163,184,0.2)" : "rgba(124,58,237,0.34)")};
`;

export const AppStatGrid = styled.div`
  display: grid;
  gap: 0.8rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
`;

export const AppStatCard = styled.div`
  padding: 1rem;
  border-radius: 18px;
  background: ${(p) => (p.$variant === "light" ? "#ffffff" : "rgba(255,255,255,0.04)")};
  border: ${(p) => (p.$variant === "light"
    ? "1px solid #e2e8f0"
    : "1px solid rgba(148,163,184,0.12)")};
`;

export const AppStatLabel = styled.div`
  color: ${(p) => (p.$variant === "light" ? "#64748b" : "rgba(255,255,255,0.56)")};
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const AppStatValue = styled.div`
  margin-top: 0.25rem;
  color: ${(p) => (p.$variant === "light" ? "#0f172a" : "#ffffff")};
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1.1;
`;

export const AppNotice = styled.div`
  padding: 0.9rem 1rem;
  border-radius: 16px;
  border: 1px solid ${(p) => (p.$warn ? "#fed7aa" : "#bfdbfe")};
  background: ${(p) => (p.$warn ? "#fff7ed" : "#eff6ff")};
  color: ${(p) => (p.$warn ? "#9a3412" : "#1d4ed8")};
  font-size: 0.88rem;
  line-height: 1.55;
`;
